const attributes = {
  areaSelector: "area-selector",
  slideSelector: "slide-selector",
  prevButtonSelector: "prev-btn-selector",
  nextButtonSelector: "next-btn-selector",
  scrollable: "scrollable",
  scrollableLeft: "scrollable-left",
  scrollableRight: "scrollable-right",
  scrolling: "scrolling",
};

const cssVars = {
  thumbWidth: "hscroll-slider-thumb-width",
  thumbShift: "hscroll-slider-thumb-shift",
  scrollProgress: "hscroll-slider-scroll-progress",
};

const defaultSelectors = {
  area: "[data-hscroll-slider-area]",
  slide: "[data-hscroll-slider-slide]",
  prevButton: "[data-hscroll-slider-button-prev]",
  nextButton: "[data-hscroll-slider-button-next]",
};

if (!customElements.get("hscroll-slider")) {
  customElements.define(
    "hscroll-slider",
    class HScrollSlider extends HTMLElement {
      #$scrollableArea;
      #$slides;
      #$buttons = [];

      connectedCallback() {
        this.#init();

        window.addEventListener("resize", this.#render.bind(this));
        this.#render();
      }

      #init() {
        this.#initScrollableArea();
        this.#initSlides();
        this.#initButtons();
      }

      #initScrollableArea() {
        let $scrollableArea = null;
        const scrollableAreaSelector =
          this.getAttribute(attributes.areaSelector) || defaultSelectors.area;
        if (scrollableAreaSelector) {
          $scrollableArea = this.querySelector(scrollableAreaSelector);
        }
        if ($scrollableArea === this.#$scrollableArea) return;

        if ($scrollableArea) {
          $scrollableArea.addEventListener("scroll", () => {
            this.#render();
            this.setAttribute(attributes.scrolling, "");
            if (this.scrollTimer !== undefined) {
              clearTimeout(this.scrollTimer);
            }
            this.scrollTimer = setTimeout(() => {
              this.removeAttribute(attributes.scrolling);
            }, 150);
          });
        }

        this.#$scrollableArea = $scrollableArea;
      }

      #initSlides() {
        let $slides = [];
        const slideSelector =
          this.getAttribute(attributes.slideSelector) || defaultSelectors.slide;
        if (slideSelector && this.#$scrollableArea) {
          $slides = [...this.#$scrollableArea.querySelectorAll(slideSelector)];
        }
        this.#$slides = $slides;
      }

      #initButtons() {
        const prevButtonSelector =
          this.getAttribute(attributes.prevButtonSelector) ||
          defaultSelectors.prevButton;
        if (prevButtonSelector) {
          this.querySelectorAll(prevButtonSelector).forEach(($button) => {
            if (this.#$buttons.includes($button)) return;
            $button.addEventListener("click", () => {
              this.goToPrevSlide();
            });
            this.#$buttons.push($button);
          });
        }

        const nextButtonSelector =
          this.getAttribute(attributes.nextButtonSelector) ||
          defaultSelectors.nextButton;
        if (nextButtonSelector) {
          this.querySelectorAll(nextButtonSelector).forEach(($button) => {
            if (this.#$buttons.includes($button)) return;
            $button.addEventListener("click", () => {
              this.goToNextSlide();
            });
            this.#$buttons.push($button);
          });
        }
      }

      // #alignPosition(isScrolledLeft) {
      // if (isScrolledLeft) this.goToPrevSlide();
      // else this.goToNextSlide();
      // this.#$scrollableArea.style.scrollSnapType = '';
      // }

      goToPrevSlide() {
        if (this.#$slides.length > 0) {
          let newScrollLeft = 0;
          const originPoint = this.#$slides[0].offsetLeft;
          const scrollLeft = this.#$scrollableArea.scrollLeft;
          const $prevSlide = [...this.#$slides].findLast(($element) => {
            return $element.offsetLeft < scrollLeft + originPoint - 1;
          });
          if ($prevSlide) {
            newScrollLeft = $prevSlide.offsetLeft - originPoint;
          }
          if (newScrollLeft < 0) {
            newScrollLeft = 0;
          }

          this.#$scrollableArea.scrollTo({
            top: 0,
            left: newScrollLeft,
            behavior: "smooth",
          });
        }
      }

      goToNextSlide() {
        if (this.#$slides.length > 0) {
          const maxScroll =
            this.#$scrollableArea.scrollWidth -
            this.#$scrollableArea.clientWidth;
          let newScrollLeft = maxScroll;
          const originPoint = this.#$slides[0].offsetLeft;
          const scrollLeft = this.#$scrollableArea.scrollLeft;
          const $nextSlide = this.#$slides.find(
            ($element) => $element.offsetLeft > scrollLeft + originPoint + 1
          );
          if ($nextSlide) {
            newScrollLeft = $nextSlide.offsetLeft - originPoint;
          }
          if (newScrollLeft > maxScroll) {
            newScrollLeft = maxScroll;
          }

          this.#$scrollableArea.scrollTo({
            top: 0,
            left: newScrollLeft,
            behavior: "smooth",
          });
        }
      }

      #render() {
        this.#renderCssVars();
        this.#renderAttributes();
      }

      #renderCssVars() {
        if (!this.#$scrollableArea) return;
        let thumbWidth = 100;
        let thumbShift = 0;
        let scrollProgress = 0;

        const clientWidth = this.#$scrollableArea.clientWidth;
        const scrollWidth = this.#$scrollableArea.scrollWidth;
        const scrollLeft = this.#$scrollableArea.scrollLeft;
        const maxScroll = scrollWidth - clientWidth;

        if (scrollWidth > clientWidth) {
          thumbWidth = (clientWidth * 100.0) / scrollWidth;
          thumbShift = (scrollLeft * 100.0) / scrollWidth;
          scrollProgress = (scrollLeft * 100.0) / maxScroll;
        }

        this.style.setProperty(`--${cssVars.thumbWidth}`, `${thumbWidth}%`);
        this.style.setProperty(`--${cssVars.thumbShift}`, `${thumbShift}%`);
        this.style.setProperty(
          `--${cssVars.scrollProgress}`,
          `${scrollProgress}%`
        );
      }

      #renderAttributes() {
        const $scrollableArea = this.#$scrollableArea;
        if (!$scrollableArea) return;

        const isScrollable =
          $scrollableArea.scrollWidth > $scrollableArea.clientWidth;
        if (isScrollable) {
          this.setAttribute(attributes.scrollable, "");

          if ($scrollableArea.scrollLeft > 0) {
            this.setAttribute(attributes.scrollableLeft, "");
          } else {
            this.removeAttribute(attributes.scrollableLeft);
          }

          if (
            $scrollableArea.scrollLeft <
            $scrollableArea.scrollWidth - $scrollableArea.clientWidth
          ) {
            this.setAttribute(attributes.scrollableRight, "");
          } else {
            this.removeAttribute(attributes.scrollableRight);
          }
        } else {
          this.removeAttribute(attributes.scrollable);
          this.removeAttribute(attributes.scrollableLeft);
          this.removeAttribute(attributes.scrollableRight);
        }
      }
    }
  );
}
